'use client';

// A compact segmented control — the editor's primary multi-choice input.
//
// SELECTION IS NOT AN ACTION. This used to carry its selected state as a solid
// `--ed-accent` fill, which handed the accent a second job on top of the one
// design-language.md §1 gives it (the current position — the eyebrow and the
// active step dot, both of which mean "where you are in the flow", not "which
// option you picked"). Two meanings for one hue is how the accent stops
// meaning anything.
//
// So the selected state is now DEPTH, not colour: a raised thumb sitting in a
// recessed track. Both tokens already existed for exactly this — surfaces.ts
// derives `hover: lift(-1)` ("pressing goes into the page") and
// `raised: lift(2)` off the page colour, so the control reads as physically
// toggled without spending a hue. The blue stays where the palette doc puts
// it, on the acoustic data.
//
// The shadow is load-bearing, not decoration. On this palette raised-vs-
// recessed is only a 1.21 contrast ratio (the accent fill it replaces was
// 4.30), so the fill alone cannot carry the state. What carries it is the
// label (3.78 dim-on-track vs 15.56 ink-on-thumb) plus real elevation. The
// recipe is the one `.ed-range`'s thumb already uses in globals.css: ink at
// low alpha via color-mix, so no new token and no hue.
//
// `w-fit` rather than `self-start`: in a flex COLUMN the control would
// otherwise stretch to the panel width and leave a long empty track beside
// its options, but `self-start` would also override `items-center` in the
// flex ROWS this sits in (RoomPanel's units, StepCeiling's Along) and
// top-align it against its label. An explicit width beats `stretch` without
// touching cross-axis alignment, so one value is correct in both.
//
// The enclosing hairline the old version drew is gone with it: a recessed fill
// already separates the control from its host, and a border on top of that is
// two separators doing one job.
//
// An option is two layers: the <button> is the HIT AREA and the thumb inside it
// is everything you see. With a mouse they are one box. Under a finger
// (`pointer: coarse`) the button grows to the app's tap floor, `--tap-target`,
// and the thumb does not. At 36x22 the footer's theme switch was a mis-tap on
// a phone, but a thumb painted at 44px reads as oversized beside the 12-13px
// text it sits with, the call that kept the header dots at 36px
// (ui/chromeDot.ts). So the vertical growth is handed back to the layout by an
// equal negative margin and overhangs the track by 8px, the way the footer's
// links grew their box and not their text. A host that clips its overflow
// closer than that clips the target with it; the account menu's theme band has
// exactly the 8px it needs. The horizontal growth is real and the thumb grows
// with it: neighbours never overlap, and along the row what you see is what
// you tap.

import type { ComponentType } from 'react';
import { FOCUS_RING_ON_SEGMENT_THUMB } from '@/components/ui/focusRing';
import { RAISED_SHADOW } from './selection';

export interface SegItem {
  value: string;
  label: string;
  /** Optional glyph before the label (lucide icons fit the signature). */
  icon?: ComponentType<{ className?: string }>;
}

interface Props {
  items: readonly SegItem[];
  value: string;
  onChange: (value: string) => void;
  /** Optional uppercase tag rendered inside the control on the left. */
  tag?: string;
  /** Base test id — the root gets it, each option gets `${testId}-${value}`. */
  testId?: string;
  /** Disables the whole control. Per-item disabling is deliberately absent:
   *  an option you cannot pick should not be offered, and the one call site
   *  that needed this (StepGenerate's mode picker mid-solve) greys out all
   *  three together. */
  disabled?: boolean;
  /** Show only each item's icon; the label stays in the accessible name. */
  iconOnly?: boolean;
}

// `--seg-thumb` is the one height every option paints at, icon or label, and
// the touch geometry below is derived from it rather than restating 24px.
const TRACK =
  'flex w-fit flex-wrap items-center gap-0.5 rounded-sm bg-[var(--ed-surface-2)] p-0.5 [--seg-thumb:1.5rem] '
  // If the options wrap, open the row gap under a finger by exactly what the
  // hit areas overhang, so a second row's targets meet the first row's
  // instead of lying over them.
  + 'pointer-coarse:gap-y-[calc(var(--tap-target)_-_var(--seg-thumb))]';

const OPTION =
  'group/seg flex items-center outline-none '
  + 'pointer-coarse:min-h-[var(--tap-target)] pointer-coarse:min-w-[var(--tap-target)] '
  + 'pointer-coarse:-my-[calc((var(--tap-target)_-_var(--seg-thumb))/2)]';

// The border is on BOTH states — transparent when idle — so gaining the thumb
// cannot shift the row by a pixel.
const THUMB =
  'inline-flex h-[var(--seg-thumb)] grow items-center justify-center gap-1.5 rounded-sm border px-2.5 '
  + `text-caption font-medium capitalize transition-colors group-active/seg:scale-[0.97] ${FOCUS_RING_ON_SEGMENT_THUMB}`;

const THUMB_ACTIVE = `border-[var(--ed-border)] bg-[var(--ed-input)] text-[var(--ed-text)] ${RAISED_SHADOW}`;
const THUMB_IDLE = 'border-transparent text-[var(--ed-text-dim)] group-hover/seg:text-[var(--ed-text)]';

export default function Segmented({ items, value, onChange, tag, testId, disabled = false, iconOnly = false }: Props) {
  return (
    <div
      data-testid={testId}
      className={`${TRACK}${disabled ? ' cursor-not-allowed opacity-60' : ''}`}
    >
      {tag && (
        <span className="px-1.5 text-overline font-semibold uppercase text-[var(--ed-text-dim)]">
          {tag}
        </span>
      )}
      {items.map((it) => {
        const active = it.value === value;
        const Icon = it.icon;
        return (
          <button
            key={it.value}
            type="button"
            data-testid={testId ? `${testId}-${it.value}` : undefined}
            data-active={active}
            aria-pressed={active}
            aria-label={iconOnly ? it.label : undefined}
            title={iconOnly ? it.label : undefined}
            disabled={disabled}
            onClick={() => onChange(it.value)}
            className={OPTION}
          >
            <span className={`${THUMB} ${active ? THUMB_ACTIVE : THUMB_IDLE}`}>
              {Icon && <Icon className="h-3.5 w-3.5" />}
              {!iconOnly && it.label}
            </span>
          </button>
        );
      })}
    </div>
  );
}
